import { action, makeObservable } from 'mobx';
import { createContext, useContext } from 'react';
import ElectionsModule from 'store/modules/elections/electionsModule';
import IFullFormPageStore from 'store/modules/full-employee-form/iFullFormPageStore';
import {
  BaseFullEmployeeFormDomainParams,
  FULL_FORM_TABS_WITH_ELECTIONS,
} from 'store/modules/full-employee-form/types';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';

/**
 * TO_DO_QUOTE_CHECKLIST:
 * Unit tests scope:
 * initiateQuoteFlow store & pages;
 * quoteFlow store & pages;
 * QuoteChecklistPage
 * QuoteFlowLayout
 */

export interface FullEmployeeFormDomainParams extends BaseFullEmployeeFormDomainParams {
  cartId: string;
}

export default class FullFormPageStore extends IFullFormPageStore<FullEmployeeFormDomainParams> {
  public readonly flowDelegate: QuoteFlowDelegate;

  protected createElectionsModule(): ElectionsModule {
    return new ElectionsModule({
      groupId: this._domainData.groupId,
      enrollmentPeriodId: this._domainData.periodId,
      currentFlow: 'quoting',
      cartId: this._domainData.cartId,
    });
  }

  protected async _fetchCoreData(): Promise<void> {
    await Promise.all([this._electionsModule.getRequiredSubInfo(), this._electionsModule.getProductLines()]);
  }

  protected getPrimaryFormId(): string | undefined {
    return this._electionsModule.requiredSubInfo?.quotingSubFormId;
  }

  public async getInitialData(): Promise<void> {
    await this.flowDelegate.getFlowInitialData();
    await super.getInitialData();
  }

  public get isInitialDataLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || super.isInitialDataLoading;
  }

  public onBackRedirect(): void {
    redirectToRoute(QuoteRouteV2Id.QUOTE_ADD_EMPLOYEES, {
      groupId: this._domainData.groupId,
      periodId: this._domainData.periodId,
      cartId: this._domainData.cartId,
    });
  }

  constructor(domainData: FullEmployeeFormDomainParams) {
    super(domainData, FULL_FORM_TABS_WITH_ELECTIONS);

    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainData.groupId,
        periodId: domainData.periodId,
        cartId: domainData.cartId,
      },
      nextPageId: QuoteRouteV2Id.QUOTE_ADD_EMPLOYEES,
    });

    makeObservable<FullFormPageStore, '_fetchCoreData'>(this, {
      onBackRedirect: action.bound,
      _fetchCoreData: action.bound,
    });
  }
}

const FullFormPageStoreContext = createContext(new FullFormPageStore({} as FullEmployeeFormDomainParams));

export const useFullFormPageStore = (): FullFormPageStore => {
  return useContext(FullFormPageStoreContext);
};

export { FullFormPageStoreContext };
